import { isEqual, cloneDeep } from 'lodash-es'

/**
 * @param {object} obj
 * @param {string} path - Dot-separated path (e.g. 'localDashboard.filters')
 * @returns {any}
 */
function getNestedValue(obj, path) {
  return path.split('.').reduce((acc, key) => acc?.[key], obj)
}

/**
 * @param {object} obj
 * @param {string} path - Dot-separated path
 * @param {any} value
 */
function setNestedValue(obj, path, value) {
  const keys = path.split('.')
  const last = keys.pop()
  const target = keys.reduce((acc, key) => acc?.[key], obj)
  if (target && last) {
    target[last] = value
  }
}

/**
 * @param {object} vm - Vue component instance
 * @returns {string} Unique key identifying what this component instance is currently displaying.
 * Prefers the `currentBoard` prop (used by Dashboard/Content/* components, where several boards of
 * different types can be displayed under the exact same route) over the route params: DashboardMainContainer
 * swaps components based on `selectedDashboard.type`, which is only updated ~150ms after `$route` changes,
 * so keying on `$route` directly would reassign this key while the outgoing component is still mounted,
 * corrupting the incoming board's cache once the outgoing one unmounts. `currentBoard` only ever reflects
 * a new board once this component instance is actually the one displaying it.
 * `currentBoard.id` alone (no `$route.name` prefix) is used here: it's a globally unique template-dashboard
 * ULID, and several routes (e.g. `dashboard_page_route` and `dashboard_dossier_page_route`) can display the
 * exact same board — prefixing with the route name would give each route its own cache for the same board.
 */
function buildStateKey(vm) {
  if (vm.currentBoard?.id) {
    return `board:${vm.currentBoard.id}`
  }
  const base = vm.$route.name
  const id = vm.$route.params?.id
  return id ? `${base}:${id}` : base
}

/**
 * Captures a plain (non-reactive) snapshot of persisted properties.
 * - cloneDeep strips Vue reactive Proxies to avoid storing live references in Vuex.
 * - Keys whose value equals the component's data() default are omitted to keep the cache lean
 *   (e.g. searchTerms='' or specific_day_enabled=false are not saved).
 * - Dotted keys (e.g. 'localDashboard.filters') have no matching flat default so they are
 *   always included when their value differs from undefined.
 * @param {object} vm - Vue component instance
 * @returns {Record<string, any>} Snapshot of persisted properties
 */
function captureState(vm) {
  const data = {}
  const defaults = vm.$options.data ? vm.$options.data.call(vm) : {}
  vm.$options.persistViewState.forEach(key => {
    const val = cloneDeep(key.includes('.') ? getNestedValue(vm, key) : vm[key])
    if (!isEqual(val, defaults[key])) {
      data[key] = val
    }
  })
  return data
}

/**
 * Persists captured state to the store, or clears the entry if everything is at its default.
 * @param {object} vm - Vue component instance
 */
function persistState(vm) {
  const captured = captureState(vm)
  if (Object.keys(captured).length > 0) {
    vm.$store.commit('viewFiltersState/SET_STATE', { routeName: vm._persistedStateKey, data: captured })
  } else {
    vm.$store.commit('viewFiltersState/CLEAR_STATE', vm._persistedStateKey)
  }
}

/**
 * Restores flat (non-dotted) persisted keys only. Dotted/nested keys (e.g.
 * 'localDashboard.filters') are NOT restored here: at mount time their root is still
 * null, and on a tab switch (the identity watcher) their root is still the PREVIOUS
 * tab's full object — writing the new tab's cached value onto it here would put the
 * new tab's filters on the wrong dashboard for one tick, racing the component's own
 * fetch. Nested keys are restored once, by the component itself, via
 * `$restoreCachedState()` called from its fetch's `.then()` after the real backend
 * object has been assigned.
 * @param {object} vm - Vue component instance
 * @param {Record<string, any>} saved - Saved state to restore
 */
function restoreState(vm, saved) {
  Object.keys(saved).forEach(key => {
    if (key.includes('.')) return
    vm[key] = saved[key]
  })
}

/**
 * Reset non-nested persisted properties to their initial data() values.
 * Nested properties (e.g. localDashboard.filters) are skipped as they get reloaded by async fetches.
 * @param {object} vm - Vue component instance
 */
function resetToDefaults(vm) {
  const defaults = vm.$options.data ? vm.$options.data.call(vm) : {}
  vm.$options.persistViewState.forEach(key => {
    if (!key.includes('.') && key in defaults) {
      vm[key] = defaults[key]
    }
  })
}

export default {
  data() {
    return {
      _initialStateMap: {}
    }
  },

  methods: {
    /**
     * Register the backend-provided initial value for a persisted key.
     * Used to track dirty state (i.e. whether the user has changed filters from their default).
     * @param {string} key - The persisted key (e.g. 'localDashboard.filters')
     * @param {any} value - The initial value from the backend
     */
    $registerInitialState(key, value) {
      this._initialStateMap[key] = cloneDeep(value)
    },

    /**
     * Returns true if the current value of key differs from its registered initial state.
     * @param {string} key
     * @returns {boolean}
     */
    $isStateDirty(key) {
      const current = key.includes('.') ? getNestedValue(this, key) : this[key]
      return !isEqual(current, this._initialStateMap[key])
    },

    /**
     * Returns a deep clone of the registered initial value for key.
     * @param {string} key
     * @returns {any}
     */
    $getInitialState(key) {
      return cloneDeep(this._initialStateMap[key])
    },

    /**
     * Registers the backend value as the dirty-check baseline for key, then overlays
     * any value cached from a previous visit to this tab within the current session.
     * Call once the object at key's root has been assigned (e.g. right after
     * `this.localDashboard = response.template_dashboard`).
     * @param {string} key - The persisted key (e.g. 'localDashboard.filters')
     * @param {any} backendValue - The value returned by the server, used as the dirty-check baseline
     */
    $restoreCachedState(key, backendValue) {
      this.$registerInitialState(key, backendValue)
      const saved = this.$store.getters['viewFiltersState/getState'](this._persistedStateKey)
      const cachedValue = saved?.[key]
      if (cachedValue === undefined) return
      if (key.includes('.')) {
        setNestedValue(this, key, cachedValue)
      } else {
        this[key] = cachedValue
      }
    }
  },

  mounted() {
    if (!this.$options.persistViewState) return
    /** @type {string} Capture state key now, as identity may change before beforeUnmount */
    this._persistedStateKey = buildStateKey(this)
    /** @type {Record<string, any> | null} Previously saved filters/search state for this view */
    const saved = this.$store.getters['viewFiltersState/getState'](this._persistedStateKey)
    if (saved) {
      restoreState(this, saved)
    }
  },

  computed: {
    /**
     * Recomputed whenever `buildStateKey` would return a different value for this instance.
     * Kept as its own computed (rather than watching `$route` or `currentBoard` directly) so the
     * watcher below fires exactly when this component's own persistence key actually needs to change.
     */
    _persistViewStateIdentity() {
      if (!this.$options.persistViewState) return null
      return buildStateKey(this)
    }
  },

  watch: {
    /**
     * Handles tab/dashboard switches where Vue reuses the component without unmounting it.
     * Saves the current state under the old key and updates the key to match the new identity,
     * so the subsequent async fetch (e.g. fetchTemplateDashboardConfig) restores the correct filters.
     */
    _persistViewStateIdentity(newKey) {
      if (!this.$options.persistViewState || !this._persistedStateKey) return
      if (newKey === this._persistedStateKey) return

      /** Save current state under the old key before switching */
      persistState(this)

      this._persistedStateKey = newKey

      /** Reset to defaults first (useful when component are not unmounted), then restore cached state if available */
      resetToDefaults(this)
      const saved = this.$store.getters['viewFiltersState/getState'](newKey)
      if (saved) {
        restoreState(this, saved)
      }
    }
  },

  beforeUnmount() {
    if (!this.$options.persistViewState || !this._persistedStateKey) return
    persistState(this)
  }
}